<template>
  <div class="row" v-if="profile">
    <div class="col-md-12">
      <!-- Back link -->
      <div class="mb-2">
        <router-link :to="{ name: 'head-hunting' }" class="text-muted small">
          <i class="fa fa-arrow-left"></i> Back to Search
        </router-link>
      </div>

      <!-- Candidate Header -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 class="mb-0">
            {{ profile.candidate.full_name }}
            <BBadge v-if="profile.candidate.deleted_at" variant="danger" class="ml-1">DELETED</BBadge>
          </h4>
          <span class="text-muted">{{ profile.candidate.email }}</span>
        </div>
        <div v-if="profile.document && profile.document.cdn">
          <BButton
            variant="outline-primary"
            size="sm"
            class="mr-2"
            @click="previewProfileResume"
          >
            <i class="fa fa-eye"></i> Preview CV
          </BButton>
          <BButton
            :href="profile.document.cdn"
            target="_blank"
            variant="primary"
            size="sm"
          >
            <i class="fa fa-download"></i> Download CV
          </BButton>
        </div>
      </div>

      <!-- Tabs -->
      <BTabs v-model="activeTab" class="folder-tabs">
        <!-- Overview Tab -->
        <BTab title="Overview" active>
          <div class="row">
            <!-- Left Column: Submitted by Candidate -->
            <div class="col-md-6">
              <div class="section mb-4 source-section">
                <h6 class="section-title">
                  <i class="fa fa-user-edit text-primary mr-2"></i>
                  Submitted by Candidate
                  <BBadge variant="light" class="ml-2 font-weight-normal">Form Data</BBadge>
                </h6>
                <table class="table table-sm table-borderless mb-0">
                  <tr>
                    <td class="text-muted" style="width: 100px;">Email:</td>
                    <td>{{ profile.candidate.email || "-" }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">Phone:</td>
                    <td>{{ profile.candidate.phone || "-" }}</td>
                  </tr>
                  <tr>
                    <td class="text-muted">City:</td>
                    <td>{{ profile.candidate.city || "-" }}</td>
                  </tr>
                  <tr v-if="profile.candidate.linkedin_url">
                    <td class="text-muted">LinkedIn:</td>
                    <td>
                      <a :href="profile.candidate.linkedin_url" target="_blank">View Profile</a>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-muted">Team:</td>
                    <td>{{ profile.candidate.team_name || "N/A" }} (ID: {{ profile.candidate.team_id }})</td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- Right Column: Extracted from Resume -->
            <div class="col-md-6">
              <div class="section mb-4 source-section extracted-section">
                <h6 class="section-title">
                  <i class="fa fa-file-alt text-info mr-2"></i>
                  Extracted from Resume
                  <BBadge
                    :variant="getStatusVariant(profile.extraction_status)"
                    class="ml-2 font-weight-normal"
                  >{{ profile.extraction_status || 'pending' }}</BBadge>
                </h6>

                <!-- Extracted Contact Info -->
                <table class="table table-sm table-borderless mb-0" v-if="hasExtractedData">
                  <tr v-if="profile.extracted_contact && profile.extracted_contact.email">
                    <td class="text-muted" style="width: 100px;">Email:</td>
                    <td>
                      {{ profile.extracted_contact.email }}
                      <BBadge
                        v-if="profile.extracted_contact.email !== profile.candidate.email"
                        variant="warning"
                        class="ml-1"
                        title="Different from submitted"
                      >differs</BBadge>
                    </td>
                  </tr>
                  <tr v-if="profile.extracted_contact && profile.extracted_contact.phone">
                    <td class="text-muted">Phone:</td>
                    <td>
                      {{ profile.extracted_contact.phone }}
                      <BBadge
                        v-if="profile.extracted_contact.phone !== profile.candidate.phone"
                        variant="warning"
                        class="ml-1"
                        title="Different from submitted"
                      >differs</BBadge>
                    </td>
                  </tr>
                  <tr v-if="profile.extracted_contact && (profile.extracted_contact.city || profile.extracted_contact.country)">
                    <td class="text-muted">Location:</td>
                    <td>
                      {{ profile.extracted_contact.city || "" }}
                      <span v-if="profile.extracted_contact.city && profile.extracted_contact.country">, </span>
                      {{ profile.extracted_contact.country || "" }}
                    </td>
                  </tr>
                  <tr v-if="profile.extracted_contact && profile.extracted_contact.linkedin">
                    <td class="text-muted">LinkedIn:</td>
                    <td>
                      <a :href="formatLinkedIn(profile.extracted_contact.linkedin)" target="_blank">
                        {{ profile.extracted_contact.linkedin }}
                      </a>
                    </td>
                  </tr>
                </table>
                <div v-else class="text-muted small py-2">
                  <i class="fa fa-info-circle mr-1"></i>
                  No contact info extracted from resume
                </div>

                <!-- Extracted Text (collapsible) -->
                <div class="mt-3" v-if="profile.resume_text">
                  <div
                    class="extracted-text-toggle"
                    @click="showExtractedText = !showExtractedText"
                  >
                    <i class="fa mr-1" :class="showExtractedText ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    <span class="text-muted small">View extracted text</span>
                  </div>
                  <div v-if="showExtractedText" class="resume-text-preview mt-2">
                    {{ profile.resume_text }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- AI Scores Summary -->
          <div class="section" v-if="scoredApplications.length > 0">
            <h6 class="section-title">
              <i class="fa fa-robot text-success mr-2"></i>
              AI Scoring Summary
              <BBadge variant="success" class="ml-2 font-weight-normal">AI Generated</BBadge>
            </h6>
            <div class="row">
              <div class="col-md-4 text-center">
                <div
                  class="score-circle"
                  :class="getScoreClass(avgScores.overall_fit)"
                >
                  <span class="score-value">{{ avgScores.overall_fit }}%</span>
                  <span class="score-label">Overall Fit</span>
                </div>
              </div>
              <div class="col-md-4 text-center">
                <div
                  class="score-circle"
                  :class="getScoreClass(avgScores.skills_match)"
                >
                  <span class="score-value">{{ avgScores.skills_match }}%</span>
                  <span class="score-label">Skills Match</span>
                </div>
              </div>
              <div class="col-md-4 text-center">
                <div
                  class="score-circle"
                  :class="getScoreClass(avgScores.location_proximity)"
                >
                  <span class="score-value"
                    >{{ avgScores.location_proximity }}%</span
                  >
                  <span class="score-label">Location</span>
                </div>
              </div>
            </div>
            <p class="text-muted text-center mb-0 mt-2">
              <small
                >Averages from {{ scoredApplications.length }} scored
                application(s)</small
              >
            </p>
          </div>
        </BTab>

        <!-- Profile Tab -->
        <BTab title="Profile">
          <!-- Source indicator -->
          <div class="alert alert-info small mb-3 d-flex align-items-center">
            <i class="fa fa-file-alt mr-2"></i>
            <span>All data below is <strong>extracted from the candidate's resume</strong> using AI parsing.</span>
          </div>

          <!-- Language Toggle -->
          <div
            class="d-flex align-items-center mb-4"
            v-if="hasBilingualContent"
          >
            <span class="mr-3 text-muted small">Language:</span>
            <div class="btn-group btn-group-sm">
              <BButton
                :variant="
                  selectedLang === 'en' ? 'primary' : 'outline-secondary'
                "
                size="sm"
                @click="selectedLang = 'en'"
              >
                EN
              </BButton>
              <BButton
                :variant="
                  selectedLang === 'fr' ? 'primary' : 'outline-secondary'
                "
                size="sm"
                @click="selectedLang = 'fr'"
              >
                FR
              </BButton>
            </div>
          </div>

          <!-- Professional Summary -->
          <div class="section mb-4" v-if="localizedSummary">
            <h6 class="section-title">Professional Summary</h6>
            <p class="mb-0">{{ localizedSummary }}</p>
          </div>

          <!-- Work History -->
          <div
            class="section mb-4"
            v-if="profile.work_history && profile.work_history.length"
          >
            <h6 class="section-title">Work History</h6>
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Position</th>
                  <th>Period</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(job, index) in profile.work_history" :key="index">
                  <td>{{ job.company || "-" }}</td>
                  <td>{{ getLocalizedValue(job, "title") }}</td>
                  <td>
                    <small
                      >{{ job.start_date || "?" }} -
                      {{ job.end_date || "Present" }}</small
                    >
                  </td>
                  <td>
                    <small v-if="job.duration_months">
                      {{ formatDuration(job.duration_months) }}
                    </small>
                    <span v-else>-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Education -->
          <div
            class="section mb-4"
            v-if="profile.education && profile.education.length"
          >
            <h6 class="section-title">Education</h6>
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Institution</th>
                  <th>Degree</th>
                  <th>Year</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(edu, index) in profile.education" :key="index">
                  <td>{{ edu.institution || "-" }}</td>
                  <td>{{ getLocalizedValue(edu, "degree") }}</td>
                  <td>{{ edu.year || "-" }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Languages & Certifications -->
          <div
            class="row"
            v-if="
              (profile.languages && profile.languages.length) ||
                (profile.certifications && profile.certifications.length)
            "
          >
            <div
              class="col-md-6 mb-4"
              v-if="profile.languages && profile.languages.length"
            >
              <h6 class="section-title">Languages</h6>
              <div
                v-for="(lang, index) in profile.languages"
                :key="index"
                class="mb-1"
              >
                {{ getLocalizedValue(lang, "language") }}
                <BBadge
                  :variant="getLanguageLevelVariant(lang.level)"
                  class="ml-2"
                >
                  {{ capitalizeFirst(lang.level) }}
                </BBadge>
              </div>
            </div>
            <div
              class="col-md-6 mb-4"
              v-if="profile.certifications && profile.certifications.length"
            >
              <h6 class="section-title">Certifications</h6>
              <BBadge
                v-for="(cert, index) in profile.certifications"
                :key="index"
                variant="secondary"
                class="mr-2 mb-1"
              >
                {{ getLocalizedName(cert) }}
              </BBadge>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="!hasProfileData" class="text-center py-4 text-muted">
            <i class="fa fa-user-circle fa-2x mb-2"></i>
            <p class="mb-0">No profile data extracted yet.</p>
          </div>
        </BTab>

        <!-- Applications Tab -->
        <BTab
          :title="
            `Applications (${
              profile.applications ? profile.applications.length : 0
            })`
          "
        >
          <BTable
            :items="profile.applications"
            :fields="applicationFields"
            hover
            show-empty
            empty-text="No applications found"
            class="mb-0"
          >
            <template #cell(offer)="{ item }">
              <span v-if="item.offer && item.offer.title">
                {{ item.offer.title }}
                <BBadge v-if="item.offer.deleted_at" variant="danger" class="ml-1">DELETED</BBadge>
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(company)="{ item }">
              <span v-if="item.offer && item.offer.company_name">
                {{ item.offer.company_name }}
                <BBadge v-if="item.offer.company_deleted_at" variant="danger" class="ml-1">DELETED</BBadge>
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(status)="{ item }">
              <BBadge :variant="getApplicationStatusVariant(item.status)">
                {{ item.status }}
              </BBadge>
              <BBadge v-if="item.deleted_at" variant="danger" class="ml-1">DELETED</BBadge>
            </template>
            <template #cell(ai_score)="{ item }">
              <BBadge
                v-if="item.ai_scores && item.ai_scores.overall_fit !== null"
                :variant="getScoreVariant(item.ai_scores.overall_fit)"
                style="cursor: help;"
                v-b-tooltip.hover
                :title="getLocalizedReasoning(item.ai_scores)"
              >
                {{ item.ai_scores.overall_fit }}%
              </BBadge>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(created_at)="{ item }">
              {{ formatDate(item.created_at) }}
            </template>
            <template #cell(actions)="{ item }">
              <div class="btn-group">
                <BButton
                  v-if="getApplicationResumeUrl(item)"
                  size="sm"
                  variant="outline-info"
                  @click="previewApplicationResume(item)"
                  title="Preview CV"
                >
                  <i class="fa fa-eye"></i>
                </BButton>
                <BButton
                  v-if="getApplicationResumeUrl(item)"
                  size="sm"
                  variant="outline-primary"
                  :href="getApplicationResumeUrl(item)"
                  target="_blank"
                  title="Download CV"
                >
                  <i class="fa fa-download"></i>
                </BButton>
                <BButton
                  size="sm"
                  variant="outline-secondary"
                  @click="showDebugModal(item)"
                  title="View Debug Info"
                >
                  <i class="fa fa-bug"></i>
                </BButton>
                <BButton
                  size="sm"
                  :variant="isRetryQueued(item.id) ? 'outline-success' : 'outline-warning'"
                  @click="retryScoring(item)"
                  :disabled="retryingApplicationId === item.id || isRetryQueued(item.id)"
                  :title="isRetryQueued(item.id) ? 'Retry Queued - Refresh to see results' : 'Retry Scoring'"
                >
                  <i
                    class="fa"
                    :class="getRetryButtonIcon(item.id)"
                  ></i>
                </BButton>
              </div>
            </template>
          </BTable>
        </BTab>
      </BTabs>
    </div>

    <!-- Resume Preview Modal -->
    <BModal
      v-model="showResumePreview"
      :title="
        profile && profile.candidate
          ? profile.candidate.full_name + ' - Resume'
          : 'Resume Preview'
      "
      size="xl"
      hide-footer
      body-class="p-0"
      @hidden="onPreviewHidden"
    >
      <div v-if="previewUrl" class="resume-preview-container">
        <iframe
          v-if="isPdf(previewUrl)"
          :src="previewUrl"
          class="resume-iframe"
        ></iframe>
        <div
          v-else-if="isPreviewDocx && docxState !== 'error'"
          ref="docxHost"
          class="resume-iframe docx-frame"
        >
          <p v-if="docxState === 'loading'" class="p-4 text-center">Loading preview…</p>
        </div>
        <iframe
          v-else-if="isPreviewDoc && docPdfState === 'ready'"
          :src="docPdfUrl"
          title="Resume preview"
          class="resume-iframe"
        ></iframe>
        <div
          v-else-if="isPreviewDoc && docPdfState === 'loading'"
          class="resume-iframe docx-frame"
        >
          <p class="p-4 text-center" role="status">Loading preview…</p>
        </div>
        <div v-else class="p-4 text-center">
          <p class="mb-3">This document type cannot be previewed directly.</p>
          <BButton :href="previewUrl" target="_blank" variant="primary">
            <i class="fa fa-download"></i> Download to view
          </BButton>
        </div>
      </div>
    </BModal>

    <!-- Debug Info Modal -->
    <BModal
      v-model="showDebug"
      title="Application Scoring Debug Info"
      size="xl"
      hide-footer
    >
      <div v-if="debugApplication">
        <!-- Scoring Summary -->
        <div class="section mb-4">
          <h6 class="section-title">Scoring Summary</h6>
          <div class="row">
            <div class="col-md-6">
              <table class="table table-sm table-borderless mb-0">
                <tr>
                  <td class="text-muted" style="width: 150px;">
                    Application ID:
                  </td>
                  <td>{{ debugApplication.id }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Position:</td>
                  <td>{{ getDebugOfferTitle() }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Company:</td>
                  <td>{{ getDebugOfferCompany() }}</td>
                </tr>
                <tr>
                  <td class="text-muted">Applied:</td>
                  <td>{{ formatDate(debugApplication.created_at) }}</td>
                </tr>
              </table>
            </div>
            <div class="col-md-6">
              <table class="table table-sm table-borderless mb-0">
                <tr>
                  <td class="text-muted" style="width: 150px;">
                    Overall Score:
                  </td>
                  <td>
                    <BBadge
                      v-if="getDebugScore('overall_fit') !== null"
                      :variant="getScoreVariant(getDebugScore('overall_fit'))"
                    >
                      {{ getDebugScore("overall_fit") }}%
                    </BBadge>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
                <tr>
                  <td class="text-muted">Skills Match:</td>
                  <td>{{ getDebugScore("skills_match") || "-" }}%</td>
                </tr>
                <tr>
                  <td class="text-muted">Location:</td>
                  <td>{{ getDebugScore("location_proximity") || "-" }}%</td>
                </tr>
                <tr>
                  <td class="text-muted">Scoring Method:</td>
                  <td>
                    <BBadge variant="info">
                      {{ getDebugMethod() }}
                    </BBadge>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>

        <!-- Scored For -->
        <div
          class="section mb-4"
          v-if="debugApplication.ai_scores"
        >
          <h6 class="section-title">Scored For</h6>
          <table class="table table-sm table-borderless mb-0">
            <tr>
              <td class="text-muted" style="width: 150px;">Job Title:</td>
              <td>
                {{ debugApplication.ai_scores.scored_for_job_title || "-" }}
              </td>
            </tr>
            <tr>
              <td class="text-muted">Location:</td>
              <td>
                {{ debugApplication.ai_scores.scored_for_location || "-" }}
              </td>
            </tr>
            <tr>
              <td class="text-muted">Required Skills:</td>
              <td>
                <span v-if="getDebugSkills()">
                  {{ getDebugSkills() }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
            </tr>
            <tr>
              <td class="text-muted">Scored At:</td>
              <td>{{ debugApplication.ai_scores.scored_at || "-" }}</td>
            </tr>
          </table>
        </div>

        <!-- AI Reasoning -->
        <div
          class="section mb-4"
          v-if="debugApplication.ai_scores && debugApplication.ai_scores.reasoning_en"
        >
          <h6 class="section-title">AI Reasoning</h6>
          <div class="mb-2">
            <strong>English:</strong>
            <p class="mb-1">{{ debugApplication.ai_scores.reasoning_en }}</p>
          </div>
          <div v-if="debugApplication.ai_scores.reasoning_fr">
            <strong>French:</strong>
            <p class="mb-0">{{ debugApplication.ai_scores.reasoning_fr }}</p>
          </div>
        </div>

        <!-- Debug Data -->
        <div class="section mb-4" v-if="hasDebugData()">
          <h6 class="section-title">
            <i class="fa fa-bug text-warning"></i> Debug Data
          </h6>

          <!-- Error if any -->
          <div
            v-if="getDebugField('error')"
            class="alert alert-danger"
          >
            <strong>Error:</strong>
            {{ getDebugField("error") }}
          </div>

          <!-- Prompt sent to AI -->
          <div class="mb-3" v-if="getDebugField('prompt')">
            <label class="font-weight-bold">Prompt Sent to AI:</label>
            <pre class="debug-pre">{{ getDebugField("prompt") }}</pre>
          </div>

          <!-- System Prompt -->
          <div class="mb-3" v-if="getDebugField('system_prompt')">
            <label class="font-weight-bold">System Prompt:</label>
            <pre class="debug-pre">{{ getDebugField("system_prompt") }}</pre>
          </div>

          <!-- Raw AI Response -->
          <div class="mb-3" v-if="getDebugField('raw_response')">
            <label class="font-weight-bold">Raw AI Response:</label>
            <pre class="debug-pre">{{ getDebugField("raw_response") }}</pre>
          </div>
        </div>

        <!-- No Debug Data -->
        <div v-if="!hasDebugData()" class="alert alert-info">
          <i class="fa fa-info-circle"></i>
          Debug data not available. This application was scored before debug
          logging was added. Click "Retry Scoring" to re-score with debug data.
        </div>

        <!-- Scoring Guide -->
        <div class="section mb-4">
          <h6
            class="section-title clickable"
            @click="showScoringGuide = !showScoringGuide"
          >
            <i
              class="fa mr-1"
              :class="showScoringGuide ? 'fa-chevron-down' : 'fa-chevron-right'"
            ></i>
            <i class="fa fa-lightbulb text-warning"></i> How AI Scoring Works
          </h6>
          <div v-if="showScoringGuide" class="scoring-guide">
            <div class="guide-section">
              <h6>Overall Fit (0-100)</h6>
              <p>
                Weighted combination: <strong>Skills Match (60%)</strong> + <strong>Location Proximity (40%)</strong>.
              </p>
              <ul>
                <li><strong>80-100:</strong> Excellent match - highly qualified and nearby</li>
                <li><strong>60-79:</strong> Good match - meets most requirements</li>
                <li><strong>40-59:</strong> Partial match - some relevant experience</li>
                <li><strong>0-39:</strong> Poor match - missing key qualifications or far away</li>
              </ul>
            </div>

            <div class="guide-section">
              <h6>Skills Match (0-100)</h6>
              <p>How well the candidate's skills align with the job's required skills.</p>
              <ul>
                <li>Compares resume skills against required skills listed in the job</li>
                <li>Higher scores when candidate has direct experience with required technologies</li>
                <li>Related/transferable skills provide partial credit</li>
              </ul>
            </div>

            <div class="guide-section">
              <h6>Location Proximity (0-100)</h6>
              <p>Distance-based score between candidate and job location (using GPS coordinates).</p>
              <ul>
                <li><strong>100:</strong> Less than 20 km</li>
                <li><strong>75:</strong> 20 – 49 km</li>
                <li><strong>40:</strong> 50 – 99 km</li>
                <li><strong>20:</strong> 100 – 199 km</li>
                <li><strong>0:</strong> 200 km or more</li>
              </ul>
            </div>

            <div class="guide-section">
              <h6>Tips for Better Scores</h6>
              <ul>
                <li>
                  <strong>Resume quality matters:</strong> Clear, well-formatted
                  resumes extract better data
                </li>
                <li>
                  <strong>Skills visibility:</strong> Candidates should list
                  relevant skills explicitly
                </li>
                <li>
                  <strong>Work history dates:</strong> Include start/end dates
                  for accurate experience calculation
                </li>
                <li>
                  <strong>Location info:</strong> Include city/province for
                  better location matching
                </li>
                <li>
                  <strong>Job posting quality:</strong> Specific required skills
                  in the job lead to better matching
                </li>
              </ul>
            </div>

            <div class="guide-section">
              <h6>Scoring Methods</h6>
              <ul>
                <li>
                  <strong>structured:</strong> Best quality - uses extracted
                  profile data (work history, skills, education)
                </li>
                <li>
                  <strong>text:</strong> Fallback - analyzes raw resume text
                  (when structured extraction fails)
                </li>
                <li>
                  <strong>haversine_only:</strong> Location distance only
                  (no resume, GPS coordinates available)
                </li>
                <li>
                  <strong>location_only:</strong> Limited - AI-estimated location
                  (no resume, no GPS coordinates)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </BModal>
  </div>
  <div v-else class="text-center py-5">
    <BSpinner></BSpinner>
    <p>Loading candidate profile...</p>
  </div>
</template>

<script>
import {
  BButton,
  BBadge,
  BTable,
  BSpinner,
  BModal,
  BTabs,
  VBTooltip,
  BTab
} from "bootstrap-vue";
import {
  isDocx,
  isDoc,
  fetchDocumentBytes,
  fetchDocPreviewUrl,
  revokeDocPreviewUrl,
  renderDocxInto,
  clearDocx
} from "@/utils/docx-preview.js";

export default {
  components: {
    BButton,
    BBadge,
    BTable,
    BSpinner,
    BModal,
    BTabs,
    BTab
  },
  directives: {
    "b-tooltip": VBTooltip
  },
  props: {
    candidateProfileId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      profile: null,
      showResumePreview: false,
      selectedLang: "en",
      activeTab: 0,
      applicationFields: [
        { key: "offer", label: "Position" },
        { key: "company", label: "Company" },
        { key: "status", label: "Status" },
        { key: "ai_score", label: "AI Score", class: "text-center" },
        { key: "source", label: "Source" },
        { key: "created_at", label: "Date" },
        { key: "actions", label: "", class: "text-right" }
      ],
      applicationPreviewDocument: null,
      docxState: "idle", // idle | loading | rendered | error
      docxToken: 0,
      docPdfState: "idle", // idle | loading | ready | error
      docPdfUrl: null,
      showDebug: false,
      debugApplication: null,
      retryingApplicationId: null,
      retryQueuedApplicationIds: [],
      showScoringGuide: false,
      showExtractedText: false
    };
  },
  computed: {
    hasExtractedContactDifferences() {
      if (!this.profile || !this.profile.extracted_contact) return false;
      const ec = this.profile.extracted_contact;
      const c = this.profile.candidate;
      return (
        (ec.email && ec.email !== c.email) ||
        (ec.phone && ec.phone !== c.phone) ||
        ec.linkedin ||
        ec.city ||
        ec.country
      );
    },
    hasExtractedData() {
      if (!this.profile || !this.profile.extracted_contact) return false;
      const ec = this.profile.extracted_contact;
      return !!(ec.email || ec.phone || ec.linkedin || ec.city || ec.country);
    },
    previewDocument() {
      if (this.applicationPreviewDocument) return this.applicationPreviewDocument;
      if (this.profile && this.profile.document && this.profile.document.cdn) {
        return this.profile.document;
      }
      return null;
    },
    previewUrl() {
      return this.previewDocument ? this.previewDocument.cdn : null;
    },
    isPreviewDocx() {
      return !!(
        this.previewDocument &&
        this.previewDocument.id &&
        isDocx(this.previewDocument.cdn, this.previewDocument.mime_type)
      );
    },
    isPreviewDoc() {
      return !!(
        this.previewDocument &&
        this.previewDocument.id &&
        isDoc(this.previewDocument.cdn, this.previewDocument.mime_type)
      );
    },
    scoredApplications() {
      if (!this.profile || !this.profile.applications) return [];
      return this.profile.applications.filter(
        app => app.ai_scores && app.ai_scores.overall_fit !== null
      );
    },
    avgScores() {
      const scored = this.scoredApplications;
      if (scored.length === 0) {
        return { overall_fit: 0, skills_match: 0, location_proximity: 0 };
      }
      const sumOverall = scored.reduce(
        (sum, app) => sum + (app.ai_scores.overall_fit || 0),
        0
      );
      const sumSkills = scored.reduce(
        (sum, app) => sum + (app.ai_scores.skills_match || 0),
        0
      );
      const sumLocation = scored.reduce(
        (sum, app) => sum + (app.ai_scores.location_proximity || 0),
        0
      );
      return {
        overall_fit: Math.round(sumOverall / scored.length),
        skills_match: Math.round(sumSkills / scored.length),
        location_proximity: Math.round(sumLocation / scored.length)
      };
    },
    hasBilingualContent() {
      if (!this.profile) return false;
      return !!(
        this.profile.summary_en ||
        this.profile.summary_fr ||
        (this.profile.work_history &&
          this.profile.work_history.some(j => j.title_en || j.title_fr))
      );
    },
    localizedSummary() {
      if (!this.profile) return null;
      if (this.selectedLang === "fr") {
        return (
          this.profile.summary_fr ||
          this.profile.summary_en ||
          this.profile.summary
        );
      }
      return (
        this.profile.summary_en ||
        this.profile.summary_fr ||
        this.profile.summary
      );
    },
    hasProfileData() {
      if (!this.profile) return false;
      return !!(
        this.localizedSummary ||
        (this.profile.work_history && this.profile.work_history.length) ||
        (this.profile.education && this.profile.education.length) ||
        (this.profile.languages && this.profile.languages.length) ||
        (this.profile.certifications && this.profile.certifications.length)
      );
    }
  },
  created() {
    this.fetchProfile();
  },
  beforeDestroy() {
    // Leaving the page with the modal open: release the .doc PDF.
    this.docxToken++;
    revokeDocPreviewUrl(this.docPdfUrl);
  },
  methods: {
    fetchProfile() {
      this.$http
        .get(`/enroll_admin/head-hunting/candidates/${this.candidateProfileId}`)
        .then(({ data }) => {
          this.profile = data;
        })
        .catch(() => {
          this.$router.push({ name: "head-hunting" });
        });
    },
    getStatusVariant(status) {
      const variants = {
        pending: "warning",
        completed: "success",
        failed: "danger"
      };
      return variants[status] || "secondary";
    },
    getScoreClass(score) {
      if (score >= 75) return "score-excellent";
      if (score >= 60) return "score-good";
      if (score >= 45) return "score-medium";
      return "score-low";
    },
    getScoreVariant(score) {
      if (score === null || score === undefined) return "secondary";
      if (score >= 75) return "success";
      if (score >= 60) return "info";
      if (score >= 45) return "warning";
      return "danger";
    },
    getApplicationStatusVariant(status) {
      const variants = {
        new: "primary",
        in_review: "info",
        interview_1: "warning",
        interview_2: "warning",
        offer_sent: "success",
        hired: "success",
        rejected: "danger"
      };
      return variants[status] || "secondary";
    },
    capitalizeFirst(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleDateString();
    },
    formatDuration(months) {
      if (!months) return "-";
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      if (years === 0) {
        return remainingMonths === 1 ? "1 month" : `${remainingMonths} months`;
      }
      if (remainingMonths === 0) {
        return years === 1 ? "1 year" : `${years} years`;
      }
      const yearStr = years === 1 ? "1 year" : `${years} years`;
      const monthStr =
        remainingMonths === 1 ? "1 month" : `${remainingMonths} months`;
      return `${yearStr}, ${monthStr}`;
    },
    getLanguageLevelVariant(level) {
      const variants = {
        native: "success",
        fluent: "primary",
        advanced: "info",
        intermediate: "warning",
        basic: "secondary"
      };
      return variants[level] || "secondary";
    },
    formatLinkedIn(url) {
      if (!url) return "#";
      if (url.startsWith("http")) return url;
      if (
        url.startsWith("linkedin.com") ||
        url.startsWith("www.linkedin.com")
      ) {
        return "https://" + url;
      }
      return "https://linkedin.com/in/" + url;
    },
    isPdf(url) {
      if (!url) return false;
      return url.toLowerCase().includes(".pdf");
    },
    getApplicationResumeDocument(application) {
      if (!application || !application.documents) return null;
      return (
        application.documents.find(
          d => d.pivot && d.pivot.type === "resume"
        ) || null
      );
    },
    getApplicationResumeUrl(application) {
      const resume = this.getApplicationResumeDocument(application);
      return resume ? resume.cdn : null;
    },
    previewApplicationResume(application) {
      const resume = this.getApplicationResumeDocument(application);
      if (resume) {
        this.applicationPreviewDocument = resume;
        this.showResumePreview = true;
        this.maybeLoadDocx();
      }
    },
    previewProfileResume() {
      this.applicationPreviewDocument = null;
      this.showResumePreview = true;
      this.maybeLoadDocx();
    },
    maybeLoadDocx() {
      if (this.isPreviewDocx) this.loadDocx(this.previewDocument.id);
      else if (this.isPreviewDoc) this.loadDocPdf(this.previewDocument.id);
    },
    loadDocPdf(documentId) {
      const token = ++this.docxToken;
      this.releaseDocPdf();
      this.docPdfState = "loading";
      fetchDocPreviewUrl(this.$http, documentId)
        .then(url => {
          // Closed or switched meanwhile: this URL is nobody's.
          if (token !== this.docxToken) return revokeDocPreviewUrl(url);
          this.docPdfUrl = url;
          this.docPdfState = "ready";
        })
        .catch(() => {
          if (token === this.docxToken) this.docPdfState = "error";
        });
    },
    releaseDocPdf() {
      revokeDocPreviewUrl(this.docPdfUrl);
      this.docPdfUrl = null;
      this.docPdfState = "idle";
    },
    loadDocx(documentId) {
      const token = ++this.docxToken;
      this.docxState = "loading";
      // The container may survive a close/reopen (header CV button): drop
      // any previously rendered document before the new fetch resolves.
      if (this.$refs.docxHost) clearDocx(this.$refs.docxHost);
      fetchDocumentBytes(this.$http, documentId)
        .then(({ data }) => this.$nextTick().then(() => ({ data })))
        .then(async ({ data }) => {
          if (token !== this.docxToken || !this.$refs.docxHost) return;
          // Kept outside $refs: BModal removes its content before @hidden fires.
          this.docxRenderedHost = this.$refs.docxHost;
          await renderDocxInto(this.$refs.docxHost, data);
          if (token === this.docxToken) this.docxState = "rendered";
        })
        .catch(() => {
          if (token === this.docxToken) this.docxState = "error";
        });
    },
    onPreviewHidden() {
      // Invalidate any in-flight load and drop the rendered content: the
      // container can survive the close (header CV falls back to profile.document).
      this.docxToken++;
      clearDocx(this.$refs.docxHost || this.docxRenderedHost);
      this.docxRenderedHost = null;
      this.docxState = "idle";
      this.releaseDocPdf();
      this.applicationPreviewDocument = null;
    },
    getLocalizedValue(obj, field) {
      if (!obj) return "-";
      const enKey = `${field}_en`;
      const frKey = `${field}_fr`;
      if (this.selectedLang === "fr") {
        return obj[frKey] || obj[enKey] || obj[field] || "-";
      }
      return obj[enKey] || obj[frKey] || obj[field] || "-";
    },
    getLocalizedName(obj) {
      if (!obj) return "-";
      if (typeof obj === "string") return obj;
      if (this.selectedLang === "fr") {
        return obj.name_fr || obj.name_en || obj.name || "-";
      }
      return obj.name_en || obj.name_fr || obj.name || "-";
    },
    getLocalizedReasoning(aiScores) {
      if (!aiScores) return "";
      const lang = this.selectedLang || "en";
      if (lang === "fr") {
        return aiScores.reasoning_fr || aiScores.reasoning || "";
      }
      return aiScores.reasoning_en || aiScores.reasoning || "";
    },
    showDebugModal(application) {
      this.debugApplication = application;
      this.showDebug = true;
    },
    retryScoring(application) {
      if (this.retryingApplicationId || this.isRetryQueued(application.id)) {
        return;
      }

      this.retryingApplicationId = application.id;
      this.$http
        .post(
          `/enroll_admin/head-hunting/applications/${application.id}/retry-scoring`,
          { re_extract: true }
        )
        .then(() => {
          this.retryQueuedApplicationIds.push(application.id);
          this.$notify({
            type: "success",
            title: "Retry Queued",
            message:
              "Re-extraction and scoring job has been queued. Refresh the page in a few seconds to see the results."
          });
        })
        .catch(err => {
          if (err.response && err.response.status === 409) {
            this.retryQueuedApplicationIds.push(application.id);
            this.$notify({
              type: "warning",
              title: "Already Queued",
              message: "Scoring is already queued for this application."
            });
          } else {
            var errorMsg = "Failed to queue retry job";
            if (err.response && err.response.data && err.response.data.error) {
              errorMsg = err.response.data.error;
            }
            this.$notify({
              type: "danger",
              title: "Error",
              message: errorMsg
            });
          }
        })
        .finally(() => {
          this.retryingApplicationId = null;
        });
    },
    isRetryQueued(applicationId) {
      return this.retryQueuedApplicationIds.indexOf(applicationId) !== -1;
    },
    getRetryButtonIcon(applicationId) {
      if (this.retryingApplicationId === applicationId) {
        return "fa-spinner fa-spin";
      }
      if (this.isRetryQueued(applicationId)) {
        return "fa-check";
      }
      return "fa-redo";
    },
    // Debug modal helper methods (Vue 2 compatible - no optional chaining)
    getDebugOfferTitle() {
      if (!this.debugApplication || !this.debugApplication.offer) return "-";
      return this.debugApplication.offer.title || "-";
    },
    getDebugOfferCompany() {
      if (!this.debugApplication || !this.debugApplication.offer) return "-";
      return this.debugApplication.offer.company_name || "-";
    },
    getDebugScore(field) {
      if (!this.debugApplication || !this.debugApplication.ai_scores)
        return null;
      return this.debugApplication.ai_scores[field];
    },
    getDebugMethod() {
      if (
        !this.debugApplication ||
        !this.debugApplication.ai_scores ||
        !this.debugApplication.ai_scores._debug
      ) {
        return "unknown";
      }
      return this.debugApplication.ai_scores._debug.method || "unknown";
    },
    getDebugSkills() {
      if (
        !this.debugApplication ||
        !this.debugApplication.ai_scores ||
        !this.debugApplication.ai_scores.scored_for_skills
      ) {
        return null;
      }
      return this.debugApplication.ai_scores.scored_for_skills.join(", ");
    },
    hasDebugData() {
      return (
        this.debugApplication &&
        this.debugApplication.ai_scores &&
        this.debugApplication.ai_scores._debug
      );
    },
    getDebugField(field) {
      if (!this.hasDebugData()) return null;
      return this.debugApplication.ai_scores._debug[field];
    }
  }
};
</script>

<style scoped>
/* Source section styling */
.source-section {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 15px;
  border-left: 3px solid #007bff;
}

.source-section.extracted-section {
  border-left-color: #17a2b8;
}

.source-section .section-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

/* Extracted text toggle */
.extracted-text-toggle {
  cursor: pointer;
  user-select: none;
  padding: 5px 0;
  border-top: 1px solid #e9ecef;
}

.extracted-text-toggle:hover {
  color: #007bff;
}

.resume-text-preview {
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  border: 1px solid #e9ecef;
}

/* Folder-style tabs */
.folder-tabs >>> .nav-tabs {
  border-bottom: none;
  margin-bottom: 0;
}

.folder-tabs >>> .nav-tabs .nav-link {
  background: transparent;
  border: 1px solid transparent;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  color: #6c757d;
  padding: 0.5rem 1rem;
}

.folder-tabs >>> .nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef transparent;
  background: rgba(255, 255, 255, 0.5);
}

.folder-tabs >>> .nav-tabs .nav-link.active {
  background: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  color: #333;
}

.folder-tabs >>> .tab-content {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0 4px 4px 4px;
  padding: 1.25rem;
}

.folder-tabs >>> .tab-content > .tab-pane {
  padding: 0;
}

/* Section titles */
.section-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
}

.table td,
.table th {
  vertical-align: middle;
  border-top: none;
}

.table tr:first-child td {
  padding-top: 0;
}

.resume-text {
  white-space: pre-wrap;
  max-height: 500px;
  overflow-y: auto;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.6;
}

.score-circle {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid;
}

.score-excellent {
  border-color: #28a745;
  background: rgba(40, 167, 69, 0.1);
}

.score-good {
  border-color: #17a2b8;
  background: rgba(23, 162, 184, 0.1);
}

.score-medium {
  border-color: #ffc107;
  background: rgba(255, 193, 7, 0.1);
}

.score-low {
  border-color: #dc3545;
  background: rgba(220, 53, 69, 0.1);
}

.score-value {
  font-size: 18px;
  font-weight: bold;
}

.score-label {
  font-size: 9px;
  color: #6c757d;
  text-transform: uppercase;
  text-align: center;
  line-height: 1.2;
}

.resume-preview-container {
  min-height: 70vh;
}

.resume-iframe {
  width: 100%;
  height: 80vh;
  border: none;
}

.docx-frame {
  overflow: auto;
  background: #fff;
}

.debug-pre {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 15px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.clickable {
  cursor: pointer;
  user-select: none;
}

.clickable:hover {
  color: #007bff;
}

.scoring-guide {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 15px;
  margin-top: 10px;
}

.guide-section {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e9ecef;
}

.guide-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.guide-section h6 {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.guide-section p {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 8px;
}

.guide-section ul {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 0;
  padding-left: 20px;
}

.guide-section li {
  margin-bottom: 4px;
}
</style>
